import { PayloadAction } from '@reduxjs/toolkit'
import { reducerCreatorProducer } from 'store/common'

import {
  IProjectDataState,
  TTimelineUserSoundsMetaData,
  IUpdateMediaItemPayload
} from 'store/types/projectData'
import { IMediaItem, MediaStatus, TPartialMediaItem } from 'types/media'
import { IAudioItem } from 'types/audio'
import { IProjectData } from 'types/projectData'
import { IUserAudio } from 'store/types/audio'
import {
  filterExistingAudios,
  mutateExistingAudioItemsInPlace,
  createDuplicatedItem,
  findAudioById,
  updateAudioWithVersion,
  updateProjectDataArray,
  getNewAudioItemsWithAdjustedPosition,
  shiftOverlappedTimelineItems,
  getCurrentLineItems
} from './utils'
import { IUpdatedTtsItemData } from 'types/text-to-speech'
import { isVisualMediaItem } from 'utils/media'
import { isAudioItem } from 'utils/audio'
import {
  TActiveTimelineItems,
  TActiveTimelineItemType
} from 'store/types/editor'
import { IAddMediaItemsPayload } from 'store/types/projectData'

const appendMediaItems = (
  state: IProjectDataState,
  action: PayloadAction<IAddMediaItemsPayload<IMediaItem>>
) => {
  if (!state.present.mediaItems) {
    state.present.mediaItems = []
  }
  state.present.mediaItems.push(...action.payload.value)
}

const updateMediaItem = (
  state: IProjectDataState,
  action: PayloadAction<IUpdateMediaItemPayload>,
  config: {
    isCheckAssetId?: boolean
  } = { isCheckAssetId: false }
) => {
  const { id, updates } = action.payload
  const mediaItems = state.present.mediaItems
  const itemIndex = mediaItems.findIndex(item => {
    const assetIdCheck = updates.assetId === item.assetId && item.id === id

    if (config.isCheckAssetId && updates.assetId) {
      return assetIdCheck
    }
    return item.id === id
  })
  const updatedItemCurrentDuration = mediaItems[itemIndex]?.duration

  if (itemIndex !== -1) {
    state.present.mediaItems[itemIndex] = {
      ...mediaItems[itemIndex],
      ...updates
    }
  }

  const hasDurationIncreased =
    updates.duration &&
    updatedItemCurrentDuration &&
    updates.duration > updatedItemCurrentDuration

  if (hasDurationIncreased) {
    shiftOverlappedTimelineItems({
      itemsArray: mediaItems,
      itemStartKey: 'startTime',
      itemDurationKey: 'duration',
      processedItemId: id
    })
  }
}

const reducers = reducerCreatorProducer<IProjectDataState>()({
  setProjectData: (state, action: PayloadAction<IProjectData>) => {
    state.present = action.payload
  },
  setDefaultDimensions: (
    state,
    action: PayloadAction<{ width: number; height: number }>
  ) => {
    const { width, height } = action.payload
    state.present.defaultWidth = width
    state.present.defaultHeight = height
  },
  setIsLoading: (state, action: PayloadAction<boolean>) => {
    state.isLoading = action.payload
  },
  addMediaItems: appendMediaItems,
  updateMediaItem,
  updateMediaItemWithoutUndoSave: (
    state,
    action: PayloadAction<IUpdateMediaItemPayload>
  ) => {
    updateMediaItem(state, action, {
      isCheckAssetId: action.payload?.isCheckAssetId ?? true
    })
  },
  uploadFromMediaLibrary: appendMediaItems,
  setMediaItems: (
    state,
    action: PayloadAction<IAddMediaItemsPayload<IMediaItem>>
  ) => {
    state.present.mediaItems = action.payload.value
  },
  removeVisualMediaItem: (state, action: PayloadAction<string>) => {
    const idToRemove = action.payload
    state.present.mediaItems = state.present.mediaItems.filter(
      item => item.id !== idToRemove
    )
  },
  splitVisualMediaItem: (
    state,
    action: PayloadAction<{
      splitTime: number
      splittingMediaId: string
    }>
  ) => {
    const { splitTime, splittingMediaId } = action.payload

    const splittingMediaItem = state.present.mediaItems.find(
      item =>
        item.id === splittingMediaId &&
        item.startTime < splitTime &&
        item.startTime + item.duration > splitTime
    )

    if (!splittingMediaItem) return

    const result = []
    for (const item of state.present.mediaItems) {
      if (item.id === splittingMediaItem.id) {
        result.push({
          ...item,
          duration: splitTime - item.startTime
        })
        result.push({
          ...item,
          duration: item.startTime + item.duration - splitTime,
          startTime: splitTime,
          id: crypto.randomUUID(),
          meta: {
            ...item.meta,
            ...(item.meta.trimStart !== undefined
              ? {
                  trimStart: item.meta.trimStart + (splitTime - item.startTime)
                }
              : {})
          }
        })
      } else {
        result.push(item)
      }
    }
    state.present.mediaItems = result
  },
  splitAudioItem: (
    state,
    action: PayloadAction<{
      splitTime: number
      splittingAudioId: string
    }>
  ) => {
    const { splitTime, splittingAudioId } = action.payload

    const splittingItem = state.present.multilineAudios.find(
      item =>
        item.id === splittingAudioId &&
        item.projectStart < splitTime &&
        item.projectStart + item.duration > splitTime
    )

    if (!splittingItem) return

    const result = []
    for (const item of state.present.multilineAudios) {
      if (item.id === splittingItem.id) {
        result.push({
          ...item,
          duration: splitTime - item.projectStart
        })
        result.push({
          ...item,
          duration: item.projectStart + item.duration - splitTime,
          projectStart: splitTime,
          mediaStart: item.mediaStart + (splitTime - item.projectStart),
          id: crypto.randomUUID()
        })
      } else {
        result.push(item)
      }
    }
    state.present.multilineAudios = result
  },
  updateUploadedMediaItems: (
    state,
    action: PayloadAction<
      { id: string; updates: TPartialMediaItem; status: MediaStatus }[]
    >
  ) => {
    action.payload.forEach(({ id, updates, status }) => {
      const mediaItemIndex = state.present.mediaItems.findIndex(
        item => item.id === id
      )
      const { meta = {}, ...restUpdates } = updates

      if (mediaItemIndex !== -1) {
        state.present.mediaItems[mediaItemIndex] = {
          ...state.present.mediaItems[mediaItemIndex],
          ...restUpdates,
          meta: {
            ...state.present.mediaItems[mediaItemIndex].meta,
            ...meta,
            status
          }
        }
      }
    })
  },
  setMultilineAudios: (state, action: PayloadAction<IAudioItem[]>) => {
    state.present.multilineAudios = action.payload
  },
  addMultiLineAudios: (
    state,
    action: PayloadAction<IAddMediaItemsPayload<IAudioItem>>
  ) => {
    state.present.multilineAudios.push(...action.payload.value)
  },
  updateAudioItems: (
    state,
    action: PayloadAction<{ ids: string[]; updates: Partial<IAudioItem> }>
  ) => {
    const { ids, updates } = action.payload
    ids.forEach(id => {
      const itemIndex = state.present.multilineAudios.findIndex(
        item => item.id === id
      )
      if (itemIndex !== -1) {
        state.present.multilineAudios[itemIndex] = {
          ...state.present.multilineAudios[itemIndex],
          ...updates
        }
      }
    })
  },
  updateUploadedAudioFilePaths: (
    state,
    action: PayloadAction<{ id: string; mediaPath: string; mediaId: number }[]>
  ) => {
    action.payload.forEach(({ id, mediaPath, mediaId }) => {
      const audio = state.present.multilineAudios.find(item => item.id === id)

      if (audio) {
        audio.mediaPath = mediaPath
        audio.assetId = `${mediaId}`
      }
    })
  },
  updateTtsAudios: (state, action: PayloadAction<IUpdatedTtsItemData[]>) => {
    const generatedTtsAudios = action.payload
    const multilineAudios = state.present.multilineAudios
    const ttsMultilineAudios = multilineAudios.filter(
      audio => audio.type === 'TTS'
    )
    const regeneratedTtsAudios = filterExistingAudios(
      generatedTtsAudios,
      ttsMultilineAudios
    )
    mutateExistingAudioItemsInPlace(regeneratedTtsAudios, ttsMultilineAudios)

    const uniqueLineIndexes = [
      ...new Set(ttsMultilineAudios.map(audio => audio.lineIndex))
    ]
    uniqueLineIndexes.forEach(lineIndex => {
      const lineItems = getCurrentLineItems(multilineAudios, lineIndex)

      shiftOverlappedTimelineItems({
        itemsArray: lineItems,
        itemStartKey: 'projectStart',
        itemDurationKey: 'duration'
      })
    })

    const newAudioItems = getNewAudioItemsWithAdjustedPosition(
      generatedTtsAudios,
      ttsMultilineAudios,
      multilineAudios
    )
    state.present.multilineAudios = [...multilineAudios, ...newAudioItems]
  },

  removeAudiosFromProject: (state, action: PayloadAction<string[]>) => {
    const ids = action.payload
    ids.forEach(id => {
      const index = state.present.multilineAudios.findIndex(
        item => item.id === id
      )
      if (index !== -1) {
        state.present.multilineAudios.splice(index, 1)
      }
    })
  },
  duplicateAudioItem: (state, action: PayloadAction<string>) => {
    const idToDuplicate = action.payload

    const duplicatedItem = createDuplicatedItem(
      state.present.multilineAudios,
      idToDuplicate,
      'lineIndex',
      'projectStart'
    )

    if (duplicatedItem) {
      state.present.multilineAudios.push(duplicatedItem)
    }
  },
  updateActiveAudioVolume: (
    state,
    action: PayloadAction<{ volume: number; activeAudioId: string }>
  ) => {
    const { volume, activeAudioId } = action.payload
    state.present.multilineAudios.forEach(item => {
      if (item.id === activeAudioId) {
        item.volume = volume
      }
    })
  },
  updateSoundVersion: (
    state,
    action: PayloadAction<{ audioId: string; version: IUserAudio }>
  ) => {
    const { audioId, version } = action.payload
    const audios = state.present.multilineAudios

    // Find the audio to update
    const audioData = findAudioById(audios, audioId)
    if (!audioData) return

    const { audio } = audioData
    const oldDuration = audio.duration

    // Update audio properties with the new version
    updateAudioWithVersion(audio, version)

    // Handle collision detection if duration increased
    if (audio.duration > oldDuration) {
      const currentLineItems = getCurrentLineItems(audios, audio.lineIndex)

      shiftOverlappedTimelineItems({
        itemsArray: currentLineItems,
        itemStartKey: 'projectStart',
        itemDurationKey: 'duration',
        processedItemId: audioId
      })
    }
  },
  saveToPast: (state, action: PayloadAction<IProjectData>) => {
    state.future = []
    state.past.push(action.payload)

    // Limit past array to prevent memory issues (keep last 50 states)
    if (state.past.length > 50) {
      state.past = state.past.slice(-50)
    }
  },
  undo: state => {
    if (state.past.length > 0) {
      // Move current present to future
      state.future.unshift(state.present)

      // Restore the last state from past
      const lastPastState = state.past.pop()!
      state.present = lastPastState
    }
  },
  redo: state => {
    if (state.future.length > 0) {
      // Move current present to past
      state.past.push(state.present)

      // Restore the first state from future
      const firstFutureState = state.future.shift()!
      state.present = firstFutureState
    }
  },
  updateUndoRedoMediaItems: (
    state,
    action: PayloadAction<
      {
        id: string
        updates: TPartialMediaItem
        status: MediaStatus
      }[]
    >
  ) => {
    const payload = action.payload

    state.past = updateProjectDataArray(
      state.past,
      payload as {
        id: string
        updates: Partial<IMediaItem>
        status: MediaStatus
      }[]
    )

    state.future = updateProjectDataArray(
      state.future,
      payload as {
        id: string
        updates: Partial<IMediaItem>
        status: MediaStatus
      }[]
    )
  },
  updateUndoRedoAudioItems: (
    state,
    action: PayloadAction<{ mediaPath: string; mediaId: number }[]>
  ) => {
    const payload = action.payload

    state.present.multilineAudios.forEach(item => {
      const updatedItem = payload.find(
        audio => audio.mediaId === Number(item.assetId)
      )
      if (updatedItem) {
        item.mediaPath = updatedItem.mediaPath
      }
    })
  },
  removeTimelineSelectedItems: (
    state,
    action: PayloadAction<TActiveTimelineItems>
  ) => {
    const items = action.payload
    const audioItems = items.filter(item => isAudioItem(item.type))
    const visualMediaItems = items.filter(item => isVisualMediaItem(item.type))

    state.present.multilineAudios = state.present.multilineAudios.filter(
      item =>
        !audioItems.some(
          audio => audio.id === item.id && audio.type === item.type
        )
    )
    state.present.mediaItems = state.present.mediaItems.filter(
      item =>
        !visualMediaItems.some(
          media => media.id === item.id && media.type === item.type
        )
    )
  },
  updateTimelineItems: (
    state,
    action: PayloadAction<
      { id: string | null; type: TActiveTimelineItemType; left: number }[]
    >
  ) => {
    const items = action.payload
    const audioItems = items.filter(item => isAudioItem(item.type))
    const visualMediaItems = items.filter(item => isVisualMediaItem(item.type))

    state.present.multilineAudios.forEach(item => {
      const updatedItem = audioItems.find(audio => audio.id === item.id)
      if (updatedItem) {
        item.projectStart = updatedItem.left
      }
    })

    state.present.mediaItems.forEach(item => {
      const updatedItem = visualMediaItems.find(media => media.id === item.id)
      if (updatedItem) {
        item.startTime = updatedItem.left
      }
    })
  },
  addProcessingItems: (
    state,
    action: PayloadAction<(IMediaItem | IAudioItem)[]>
  ) => {
    const newItems = state.processingItems.length
      ? action.payload.filter(
          item =>
            !state.processingItems.some(
              processingItem => processingItem.id === item.id
            )
        )
      : action.payload

    if (newItems.length) {
      state.processingItems.push(...newItems)
    }
  },
  removeProcessingItems: (state, action: PayloadAction<string[]>) => {
    state.processingItems = state.processingItems.filter(
      item => !action.payload.includes(item.id)
    )
  },
  setTimelineUserSoundsMetaData: (
    state,
    action: PayloadAction<TTimelineUserSoundsMetaData>
  ) => {
    state.timelineUserSoundsMetaData = {
      ...(state.timelineUserSoundsMetaData || {}),
      ...action.payload
    }
  },
  setSoundPeaks: (
    state,
    action: PayloadAction<{ assetId: string; peaks: number[][] }>
  ) => {
    const { assetId, peaks } = action.payload
    const existing = state.timelineUserSoundsMetaData?.[assetId]
    if (!existing) return
    state.timelineUserSoundsMetaData = {
      ...state.timelineUserSoundsMetaData,
      [assetId]: { ...existing, peaks }
    }
  }
})

export default reducers
